import { Avatar, Box, Typography } from '@mui/material';
import { TabButton, TitleText } from 'Application/components';
import { BritamAdminAgencyTab } from 'Application/models';
import { FC, useEffect, useState } from 'react';
import MainCard from 'ui-component/cards/MainCard';
import { BritamAgencyAdminCredit, BritamAgencyAdminProfile } from './tabs';
import { useAgencyStore, useCreateUserStore, useCreditStore } from 'Application/store';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from 'ui-component/Loader';
import { getBackgroundColor } from 'Application/utils';
import AgencyUsersList from './tabs/BritamAgencyAdminUsers';
import { CompletedTransactions } from 'Application/pages/TravelPortal/Dashboard/shared/Quotes/Kenya/CompletedTransactions';
import { GeneratedQuotes } from 'Application/pages/TravelPortal/Dashboard/shared/Quotes/Kenya/FailedQuotes';
import BackTo from 'Application/components/NavigateBack';
import { AgencyLocalTravel } from './tabs/AgencyLocalTravel';

interface BritamAdminAgencyProps {}

const getInitials = (name: string) => {
    if (!name) return ''; // Check if name is undefined or null
    const words = name.split(' ').filter((word) => word !== '');

    return words.length > 1 ? words[0][0] + words[1][0] : words[0][0];
};

export const BritamAdminAgency: FC<BritamAdminAgencyProps> = () => {
    const [activeTab, setActiveTab] = useState<BritamAdminAgencyTab>(BritamAdminAgencyTab.Profile);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);

    const { getSingleAgency, singleAgencyData, isAgencyLoading } = useAgencyStore((state) => ({
        getSingleAgency: state.getSingleAgency,
        singleAgencyData: state.singleAgencyData,
        isAgencyLoading: state.isAgencyLoading
    }));

    const { getCredit, getCreditData } = useCreditStore((state) => ({
        getCredit: state.getCredit,
        getCreditData: state.getCreditData
    }));

    const { getAgecyUsersList, isAgencyUsersLoading, isError, error } = useCreateUserStore((state) => ({
        isLoading: state.isLoading,
        isAgencyUsersLoading: state.isAgencyUsersLoading,
        isError: state.isError,
        error: state.error,
        getAgecyUsersList: state.getAgecyUsersList
    }));

    const { agencyId } = useParams<{ agencyId: any }>();
    const navigate = useNavigate();

    const toggleSelectedItem = (value: BritamAdminAgencyTab) => {
        setActiveTab(value);
    };

    useEffect(() => {
        if (!agencyId) return navigate('/britam-admin/agency');

        getSingleAgency(agencyId);
    }, [agencyId]);

    useEffect(() => {
        if (singleAgencyData?.name) {
            getAgecyUsersList(singleAgencyData.name);
            getCredit(singleAgencyData.name);
        }
    }, [singleAgencyData?.name]);

    if (isAgencyLoading || (isAgencyUsersLoading && !(openDialog || openModal))) {
        return <Loader />;
    }

    if (!singleAgencyData || !singleAgencyData.name) {
        return <Typography>Agency not found</Typography>; // You can display a more user-friendly message here
    }

    const initials = getInitials(singleAgencyData.name);
    const backgroundColor = getBackgroundColor(initials[0]);

    const renderActiveTab = () => {
        switch (activeTab) {
            case BritamAdminAgencyTab.Credit:
                return (
                    <BritamAgencyAdminCredit
                        currentAmount={getCreditData.creditLimit}
                        remainingAmount={getCreditData.balance}
                        name={singleAgencyData.name}
                        currency={getCreditData.currency}
                    />
                );
            case BritamAdminAgencyTab.Profile:
                return <BritamAgencyAdminProfile data={singleAgencyData} />;
            case BritamAdminAgencyTab.Users:
                // return <AgencyUsersList data={agencyUserData} isError={isError} error={error} name={singleAgencyData.name} />;
                return (
                    <AgencyUsersList
                        name={singleAgencyData.name}
                        openState={{
                            dialog: { open: openDialog, setOpen: setOpenDialog },
                            modal: { open: openModal, setOpen: setOpenModal }
                        }}
                    />
                );
            case BritamAdminAgencyTab.Policies:
                return <CompletedTransactions agencyName={singleAgencyData.name} />;
            case BritamAdminAgencyTab.Quotes:
                return <GeneratedQuotes agencyId={agencyId} />;
            case BritamAdminAgencyTab.Local_Travel:
                return singleAgencyData.intermediaryLocalTravelStatus ? <AgencyLocalTravel agencyName={singleAgencyData.name} /> : null;
            default:
                return null;
        }
    };

    return (
        <>
            <BackTo title="Back" />

            <MainCard>
                <Box className="flex items-center space-x-2 mb-4">
                    <Avatar
                        className={`border border-gray-200 ${backgroundColor}`}
                        alt={singleAgencyData.name}
                        sx={{ width: 56, height: 56 }}
                    >
                        {initials}
                    </Avatar>
                    <TitleText text={singleAgencyData.name} />
                </Box>
                <Box className="flex flex-wrap items-center space-x-1 mb-4">
                    <TabButton
                        isActive={activeTab === BritamAdminAgencyTab.Profile}
                        text={'Profile'}
                        onClick={() => toggleSelectedItem(BritamAdminAgencyTab.Profile)}
                    />
                    <TabButton
                        isActive={activeTab === BritamAdminAgencyTab.Users}
                        text={'Users'}
                        onClick={() => toggleSelectedItem(BritamAdminAgencyTab.Users)}
                    />
                    <TabButton
                        isActive={activeTab === BritamAdminAgencyTab.Policies}
                        text={'Policies'}
                        onClick={() => toggleSelectedItem(BritamAdminAgencyTab.Policies)}
                    />
                    <TabButton
                        isActive={activeTab === BritamAdminAgencyTab.Quotes}
                        text={'Quotes'}
                        onClick={() => toggleSelectedItem(BritamAdminAgencyTab.Quotes)}
                    />
                    <TabButton
                        isActive={activeTab === BritamAdminAgencyTab.Credit}
                        text={'Credit'}
                        onClick={() => toggleSelectedItem(BritamAdminAgencyTab.Credit)}
                    />
                    {singleAgencyData.intermediaryLocalTravelStatus && (
                        <TabButton
                            isActive={activeTab === BritamAdminAgencyTab.Local_Travel}
                            text={'Local Travel'}
                            onClick={() => toggleSelectedItem(BritamAdminAgencyTab.Local_Travel)}
                        />
                    )}
                </Box>
                {renderActiveTab()}
            </MainCard>
        </>
    );
};
